import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { map } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { MainMenuComponentItems, MainMenuItemGroup } from './main-menu-items';
import { MainMenuStateService } from './main-menu-state.service';

@Component({
  selector: 'glx-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatIconButton,
    MatExpansionModule,
    MatListModule,
    MatDivider,
    MatTooltip,
    RouterModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NgTemplateOutlet,
  ],
})
export class MainMenuComponent implements OnInit {
  @Input()
  parentGroup!: string | undefined;
  step = signal('');
  showAllRoutes = signal<boolean | null>(false);
  mainMenuList = MainMenuComponentItems;
  hoveredGroup = signal<string | null>(null);
  router = inject(Router);
  private auth = inject(AuthService);
  private menuState = inject(MainMenuStateService);
  private breakpointObserver = inject(BreakpointObserver);
  private closeTimer?: ReturnType<typeof setTimeout>;

  collapsed = this.menuState.collapsed;

  isMobile = toSignal(
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .pipe(map(s => s.matches)),
    { initialValue: false }
  );

  showRail = computed(() => this.collapsed() && !this.isMobile());

  flyoutPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 8,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 8,
    },
  ];

  ngOnInit() {
    this.auth.user.subscribe(user => {
      if (
        user?.login === 'conor.omalley' ||
        user?.login === 'lmeola' ||
        user?.login === 'proux'
      ) {
        this.showAllRoutes.set(true);
      }
      this.mainMenuList = MainMenuComponentItems.map(group => ({
        ...group,
        children: group.children.filter(child => {
          const hasItemPermission =
            !child.requiredPermission ||
            !!user?.permissions?.includes(child.requiredPermission);

          return hasItemPermission;
        }),
      }));

      const parentGroup = this.mainMenuList.find(el =>
        el.children.some(
          el => el.route === this.router.routerState.snapshot.url
        )
      );
      parentGroup?.iconName && this.step.set(parentGroup.iconName);
    });
  }

  setStep(name: string) {
    this.step.set(name);
  }

  isGroupActive(item: MainMenuItemGroup): boolean {
    return item.iconName === this.step();
  }

  openGroup(iconName: string) {
    clearTimeout(this.closeTimer);
    this.hoveredGroup.set(iconName);
  }

  scheduleClose() {
    this.closeTimer = setTimeout(() => this.hoveredGroup.set(null), 150);
  }

  toggleCollapsed() {
    this.menuState.toggle();
    this.hoveredGroup.set(null);
  }
}
